<script setup lang="ts">
import type { ConsultantKpiPeriod } from "@asap/shared/schemas/team-member-data";
import { AwButton, AwDatepicker } from "@asap/design";
import { endOfDay, format, startOfWeek } from "date-fns";
import { fr } from "date-fns/locale";

type MonthPickerValue = Record<"month" | "year" | "day", number>;

const referenceDate = defineModel<Date>({ required: true });

withDefaults(
  defineProps<{
    period: ConsultantKpiPeriod;
    disabled?: boolean;
  }>(),
  { disabled: false }
);

const maxDate = endOfDay(new Date());

const monthState = computed<MonthPickerValue>(() => ({
  year: referenceDate.value.getFullYear(),
  month: referenceDate.value.getMonth(),
  day: 1,
}));

const weekStart = computed(() => startOfWeek(referenceDate.value, { weekStartsOn: 1 }));

const weekModel = computed(() => [format(weekStart.value, "yyyy-MM-dd'T'HH:mm:ss")]);

const formattedMonthLabel = computed(() =>
  referenceDate.value
    .toLocaleDateString("fr-FR", { month: "long", year: "numeric" })
    .replace(/^\w/, (character) => character.toUpperCase())
);

const formattedWeekLabel = computed(() => `Semaine du ${format(weekStart.value, "d MMMM yyyy", { locale: fr })}`);

const isMonthYear = (value: unknown): value is Pick<MonthPickerValue, "month" | "year"> => {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return false;
  if (!("year" in value) || !("month" in value)) return false;
  return typeof value.year === "number" && typeof value.month === "number";
};

const handleMonthUpdate = (value: unknown) => {
  if (!isMonthYear(value)) return;
  referenceDate.value = new Date(value.year, value.month, 1, 2, 0, 0, 0);
};

const handleWeekUpdate = (value: unknown) => {
  if (!Array.isArray(value)) return;
  const weekStartDate = value[0];
  if (!(weekStartDate instanceof Date)) return;
  referenceDate.value = startOfWeek(weekStartDate, { weekStartsOn: 1 });
};

const ui = {
  triggerLabel: "font-medium text-secondary-900",
};
</script>

<template>
  <AwDatepicker
    v-if="period === 'month'"
    :model-value="monthState"
    :month-picker="true"
    :auto-apply="true"
    format="MM/yyyy"
    :max-date="maxDate"
    :disabled="disabled"
    @update:model-value="handleMonthUpdate"
  >
    <template #trigger>
      <AwButton
        variant="outline"
        color="secondary"
        class="h-[34px] min-w-[140px]"
        leading-icon="i-ph-calendar-dot"
        :disabled="disabled"
      >
        <span :class="ui.triggerLabel">
          {{ formattedMonthLabel }}
        </span>
      </AwButton>
    </template>
  </AwDatepicker>

  <AwDatepicker
    v-else
    :model-value="weekModel"
    week-picker
    :week-start="1"
    :auto-apply="true"
    :max-date="maxDate"
    :disabled="disabled"
    @update:model-value="handleWeekUpdate"
  >
    <template #trigger>
      <AwButton
        variant="outline"
        color="secondary"
        class="h-[34px] min-w-[200px]"
        leading-icon="i-ph-calendar-dot"
        :disabled="disabled"
      >
        <span :class="ui.triggerLabel">
          {{ formattedWeekLabel }}
        </span>
      </AwButton>
    </template>
  </AwDatepicker>
</template>
